import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Alert, Snackbar } from "@mui/material";
import type { AlertColor } from "@mui/material";

export type ToastSeverity = AlertColor;

export type ToastPosition = {
  vertical: "top" | "bottom";
  horizontal: "left" | "center" | "right";
};

export interface ToastOptions {
  severity?: ToastSeverity;
  duration?: number;
  position?: ToastPosition;
}

export interface ToastConfig extends ToastOptions {
  message: string;
}

export const DEFAULT_TOAST_DURATION_MS = 5000;

export const DEFAULT_TOAST_POSITION: ToastPosition = {
  vertical: "bottom",
  horizontal: "right",
};

interface ToastProps extends ToastConfig {
  open: boolean;
  onClose: () => void;
}

export const Toast: React.FC<ToastProps> = ({
  open,
  message,
  severity = "success",
  duration = DEFAULT_TOAST_DURATION_MS,
  position = DEFAULT_TOAST_POSITION,
  onClose,
}) => (
  <Snackbar
    open={open}
    autoHideDuration={duration}
    onClose={(_, reason) => {
      if (reason === "clickaway") return;
      onClose();
    }}
    anchorOrigin={position}
    sx={{ mb: 2, mr: 2, zIndex: (theme) => theme.zIndex.snackbar + 1 }}
  >
    <Alert
      onClose={onClose}
      severity={severity}
      variant="filled"
      sx={{ width: "100%", minWidth: 280 }}
    >
      {message}
    </Alert>
  </Snackbar>
);

interface ToastContextValue {
  showToast: (message: string, options?: ToastOptions) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toastState, setToastState] = useState<{
    open: boolean;
    config: ToastConfig;
  }>({
    open: false,
    config: { message: "" },
  });

  const hideToast = useCallback(() => {
    setToastState((prev) => ({ ...prev, open: false }));
  }, []);

  const showToast = useCallback((message: string, options?: ToastOptions) => {
    setToastState({
      open: true,
      config: {
        message,
        severity: options?.severity ?? "success",
        duration: options?.duration ?? DEFAULT_TOAST_DURATION_MS,
        position: options?.position ?? DEFAULT_TOAST_POSITION,
      },
    });
  }, []);

  const value = useMemo(
    () => ({ showToast, hideToast }),
    [showToast, hideToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast
        open={toastState.open}
        message={toastState.config.message}
        severity={toastState.config.severity}
        duration={toastState.config.duration}
        position={toastState.config.position}
        onClose={hideToast}
      />
    </ToastContext.Provider>
  );
};

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
